import {
  createCountry,
  createCurrency,
  createStateProvince,
  deleteCountry,
  deleteCurrency,
  deleteStateProvince,
  updateCountry,
  updateCurrency,
  updateStateProvince,
} from '@/api/reference-admin';
import type { EntityConfig } from '@/components/modals/reference-admin-types';

export const currencyEntityConfig: EntityConfig = {
  name: 'Currencies',
  singularName: 'Currency',
  fields: [
    {
      key: 'name',
      label: 'Name',
      type: 'text',
      required: true,
      placeholder: 'US Dollar',
      maxLength: 50, // VARCHAR(50)
    },
    {
      key: 'code',
      label: 'Code',
      type: 'text',
      required: true,
      placeholder: 'USD',
      maxLength: 10, // VARCHAR(10) - ISO 4217
      minLength: 3, // ISO 4217 codes are typically 3 characters
    },
    {
      key: 'symbol',
      label: 'Symbol',
      type: 'text',
      placeholder: '$',
      maxLength: 5, // VARCHAR(5)
    },
    {
      key: 'decimal_places',
      label: 'Decimals',
      type: 'number',
      min: 0,
      max: 32767, // SMALLINT max value
      defaultValue: 2,
    },
    {
      key: 'is_crypto',
      label: 'Crypto',
      type: 'checkbox',
      defaultValue: false,
    },
    {
      key: 'is_active',
      label: 'Active',
      type: 'checkbox',
      defaultValue: true,
    },
  ],
  createFn: (data: Record<string, unknown>) =>
    createCurrency(data as Parameters<typeof createCurrency>[0]),
  updateFn: (id: number, data: Record<string, unknown>) =>
    updateCurrency(id, data as Parameters<typeof updateCurrency>[1]),
  deleteFn: deleteCurrency,
};

export const countryEntityConfig: EntityConfig = {
  name: 'Countries',
  singularName: 'Country',
  fields: [
    {
      key: 'name',
      label: 'Name',
      type: 'text',
      required: true,
      placeholder: 'United States',
      maxLength: 100, // VARCHAR(100)
    },
    {
      key: 'code',
      label: 'Code',
      type: 'text',
      required: true,
      placeholder: 'US',
      maxLength: 2, // CHAR(2) - ISO 3166-1 Alpha-2
      minLength: 2, // ISO country codes are exactly 2 characters
    },
    {
      key: 'code_alpha3',
      label: 'Alpha-3',
      type: 'text',
      placeholder: 'USA',
      maxLength: 3, // CHAR(3) - ISO 3166-1 Alpha-3
      minLength: 3, // ISO Alpha-3 codes are exactly 3 characters
    },
    {
      key: 'phone_code',
      label: 'Phone Code',
      type: 'text',
      placeholder: '+1',
      maxLength: 10, // VARCHAR(10)
    },
    {
      key: 'currency_code',
      label: 'Currency Code',
      type: 'select',
      placeholder: 'Select currency...',
      options: [], // Will be populated dynamically
    },
    {
      key: 'is_active',
      label: 'Active',
      type: 'checkbox',
      defaultValue: true,
    },
  ],
  createFn: (data: Record<string, unknown>) =>
    createCountry(data as Parameters<typeof createCountry>[0]),
  updateFn: (id: number, data: Record<string, unknown>) =>
    updateCountry(id, data as Parameters<typeof updateCountry>[1]),
  deleteFn: deleteCountry,
};

export const stateProvinceEntityConfig: EntityConfig = {
  name: 'States/Provinces',
  singularName: 'State/Province',
  fields: [
    {
      key: 'name',
      label: 'Name',
      type: 'text',
      required: true,
      placeholder: 'California',
      maxLength: 100, // VARCHAR(100)
    },
    {
      key: 'code',
      label: 'Code',
      type: 'text',
      required: true,
      placeholder: 'CA',
      maxLength: 10,
    },
    {
      key: 'country_code',
      label: 'Country Code',
      type: 'select',
      required: true,
      placeholder: 'Select country...',
      options: [], // Will be populated dynamically
    },
    {
      key: 'subdivision_type',
      label: 'Type',
      type: 'select',
      required: true,
      placeholder: 'Select type',
      options: [
        { value: 'STATE', label: 'State' },
        { value: 'PROVINCE', label: 'Province' },
        { value: 'TERRITORY', label: 'Territory' },
        { value: 'REGION', label: 'Region' },
        { value: 'DISTRICT', label: 'District' },
        { value: 'CANTON', label: 'Canton' },
        { value: 'PREFECTURE', label: 'Prefecture' },
      ],
    },
    {
      key: 'is_active',
      label: 'Active',
      type: 'checkbox',
      defaultValue: true,
    },
  ],
  createFn: (data: Record<string, unknown>) =>
    createStateProvince(data as Parameters<typeof createStateProvince>[0]),
  updateFn: (id: number, data: Record<string, unknown>) =>
    updateStateProvince(id, data as Parameters<typeof updateStateProvince>[1]),
  deleteFn: deleteStateProvince,
};
